import * as Tone from 'tone';

/**
 * ARP 2600 - Semi-modular synthesizer (1971)
 *
 * Features:
 * - 3 VCOs (saw/square/pulse with PWM)
 * - 24dB lowpass filter (Moog-style ladder)
 * - 2 ADSR envelope generators
 * - LFO with multiple destinations
 * - Ring modulator
 * - Noise generator (white/pink)
 * - Pre-wired default routing (works without patches)
 */
export class ARP2600 {
    constructor(options = {}) {
        this.performance = options.performance || { lowPower: false };
        this.isPowered = true;
        this.mixerChannel = null;

        // Default parameters
        this._params = {
            // VCO 1
            vco1Freq: 440,
            vco1Fine: 0,           // -100 to +100 cents
            vco1Wave: 'sawtooth',
            vco1PulseWidth: 0.5,
            vco1Level: 0.8,

            // VCO 2
            vco2Freq: 440,
            vco2Fine: 0,
            vco2Wave: 'sawtooth',
            vco2PulseWidth: 0.5,
            vco2Level: 0.6,
            vco2Sync: false,       // Sync to VCO1

            // VCO 3 (often used as LFO)
            vco3Freq: 440,
            vco3Fine: 0,
            vco3Wave: 'sawtooth',
            vco3PulseWidth: 0.5,
            vco3Level: 0.4,
            vco3LowFreq: false,    // LF mode (0.1-30 Hz)

            // Noise
            noiseType: 'white',    // 'white' or 'pink'
            noiseLevel: 0,

            // Ring Modulator
            ringModLevel: 0,

            // VCF (24dB ladder filter)
            filterCutoff: 5000,
            filterResonance: 1,
            filterEnvAmount: 0.5,
            filterKeyTrack: 0.5,

            // ENV 1 (ADSR - typically for filter)
            env1Attack: 0.01,
            env1Decay: 0.3,
            env1Sustain: 0.5,
            env1Release: 0.5,

            // ENV 2 (AR - typically for VCA)
            env2Attack: 0.01,
            env2Decay: 0.2,
            env2Sustain: 0.7,
            env2Release: 0.3,

            // LFO
            lfoRate: 5,
            lfoWave: 'triangle',
            lfoToVco: 0,           // Vibrato amount
            lfoToFilter: 0,        // Filter wobble
            lfoToVca: 0,           // Tremolo

            // Master
            volume: 0.7,
            portamento: 0
        };

        this._currentNote = 'C3';
        this._initAudio();
    }

    _initAudio() {
        // === VCO 1 ===
        this.vco1 = new Tone.Oscillator({
            frequency: this._params.vco1Freq,
            type: this._params.vco1Wave,
            detune: this._params.vco1Fine
        });
        this.vco1Gain = new Tone.Gain(this._params.vco1Level);

        // === VCO 2 ===
        this.vco2 = new Tone.Oscillator({
            frequency: this._params.vco2Freq,
            type: this._params.vco2Wave,
            detune: this._params.vco2Fine
        });
        this.vco2Gain = new Tone.Gain(this._params.vco2Level);

        // === VCO 3 ===
        this.vco3 = new Tone.Oscillator({
            frequency: this._params.vco3Freq,
            type: this._params.vco3Wave,
            detune: this._params.vco3Fine
        });
        this.vco3Gain = new Tone.Gain(this._params.vco3Level);

        // === Noise Generator ===
        this.noise = new Tone.Noise(this._params.noiseType);
        this.noiseGain = new Tone.Gain(this._params.noiseLevel);

        // === Ring Modulator (VCO1 * VCO2) ===
        this.ringMod = new Tone.Multiply();
        this.ringModGain = new Tone.Gain(this._params.ringModLevel);

        // === VCO Mixer ===
        this.vcoMixer = new Tone.Gain(1);

        // === VCF (24dB Ladder Filter) ===
        this.filter = new Tone.Filter({
            frequency: this._params.filterCutoff,
            type: 'lowpass',
            rolloff: -24,
            Q: this._params.filterResonance
        });

        // Filter envelope scaling
        this.filterEnvGain = new Tone.Gain(this._params.filterEnvAmount * 5000);

        // === Envelope 1 (Filter) ===
        this.env1 = new Tone.Envelope({
            attack: this._params.env1Attack,
            decay: this._params.env1Decay,
            sustain: this._params.env1Sustain,
            release: this._params.env1Release
        });

        // === Envelope 2 (VCA) ===
        this.env2 = new Tone.AmplitudeEnvelope({
            attack: this._params.env2Attack,
            decay: this._params.env2Decay,
            sustain: this._params.env2Sustain,
            release: this._params.env2Release
        });

        // === LFO ===
        this.lfo = new Tone.LFO({
            frequency: this._params.lfoRate,
            type: this._params.lfoWave,
            min: -1,
            max: 1
        });

        // LFO modulation gains
        this.lfoVcoGain = new Tone.Gain(this._params.lfoToVco * 50);  // Vibrato in Hz
        this.lfoFilterGain = new Tone.Gain(this._params.lfoToFilter * 2000);  // Filter mod
        this.lfoVcaGain = new Tone.Gain(this._params.lfoToVca * 0.5);  // Tremolo

        // === Output ===
        this.outputGain = new Tone.Gain(this._params.volume);

        // === Connect default routing ===
        this._connectDefaultRouting();
    }

    _connectDefaultRouting() {
        // VCOs to mixer
        this.vco1.connect(this.vco1Gain);
        this.vco2.connect(this.vco2Gain);
        this.vco3.connect(this.vco3Gain);

        this.vco1Gain.connect(this.vcoMixer);
        this.vco2Gain.connect(this.vcoMixer);
        this.vco3Gain.connect(this.vcoMixer);

        // Noise to mixer
        this.noise.connect(this.noiseGain);
        this.noiseGain.connect(this.vcoMixer);

        // Ring mod (VCO1 * VCO2) - use level-controlled outputs
        this.vco1Gain.connect(this.ringMod);
        this.vco2Gain.connect(this.ringMod.factor);
        this.ringMod.connect(this.ringModGain);
        this.ringModGain.connect(this.vcoMixer);

        // Mixer -> Filter -> VCA Envelope -> Output
        this.vcoMixer.connect(this.filter);
        this.filter.connect(this.env2);
        this.env2.connect(this.outputGain);

        // Filter envelope modulation
        this.env1.connect(this.filterEnvGain);
        this.filterEnvGain.connect(this.filter.frequency);

        // LFO routing
        this.lfo.connect(this.lfoVcoGain);
        this.lfo.connect(this.lfoFilterGain);
        this.lfo.connect(this.lfoVcaGain);

        // LFO to VCO frequencies (vibrato)
        this.lfoVcoGain.connect(this.vco1.frequency);
        this.lfoVcoGain.connect(this.vco2.frequency);
        this.lfoVcoGain.connect(this.vco3.frequency);

        // LFO to filter (wobble)
        this.lfoFilterGain.connect(this.filter.frequency);

        // LFO to VCA (tremolo) - via output gain
        this.lfoVcaGain.connect(this.outputGain.gain);

        // Start oscillators and LFO
        this._startOscillators();
    }

    _startOscillators() {
        this.vco1.start();
        this.vco2.start();
        this.vco3.start();
        this.noise.start();
        this.lfo.start();
    }

    _stopOscillators() {
        this.vco1.stop();
        this.vco2.stop();
        this.vco3.stop();
        this.noise.stop();
        this.lfo.stop();
    }

    connectTo(destination) {
        this.outputGain.connect(destination);
    }

    setMixerChannel(channel) {
        this.mixerChannel = channel;
    }

    // === Power Management ===
    setPower(on) {
        this.isPowered = on;
        if (on) {
            this.outputGain.gain.value = this._params.volume;
            if (this.performance.lowPower) {
                this._startOscillators();
            }
        } else {
            this.outputGain.gain.value = 0;
            if (this.performance.lowPower) {
                this._stopOscillators();
            }
        }
    }

    // === Note Control ===
    triggerAttackRelease(note, duration, time, velocity = 1) {
        if (!this.isPowered) return;

        const freq = Tone.Frequency(note).toFrequency();
        const now = time || Tone.now();

        // Apply portamento
        const portaTime = this._params.portamento;
        if (portaTime > 0) {
            this.vco1.frequency.rampTo(freq + this._params.vco1Fine / 100, portaTime, now);
            this.vco2.frequency.rampTo(freq + this._params.vco2Fine / 100, portaTime, now);
            if (!this._params.vco3LowFreq) {
                this.vco3.frequency.rampTo(freq + this._params.vco3Fine / 100, portaTime, now);
            }
        } else {
            this.vco1.frequency.setValueAtTime(freq + this._params.vco1Fine / 100, now);
            this.vco2.frequency.setValueAtTime(freq + this._params.vco2Fine / 100, now);
            if (!this._params.vco3LowFreq) {
                this.vco3.frequency.setValueAtTime(freq + this._params.vco3Fine / 100, now);
            }
        }

        // Trigger envelopes
        this.env1.triggerAttackRelease(duration, now);
        this.env2.triggerAttackRelease(duration, now);

        this._currentNote = note;
    }

    // === Getters ===
    getParams() {
        return { ...this._params };
    }

    // === Setters for all parameters ===

    // VCO 1
    setVco1Wave(wave) {
        this._params.vco1Wave = wave;
        this.vco1.type = wave;
    }

    setVco1Fine(cents) {
        this._params.vco1Fine = cents;
        this.vco1.detune.value = cents;
    }

    setVco1Level(level) {
        this._params.vco1Level = level;
        this.vco1Gain.gain.value = level;
    }

    setVco1PulseWidth(pw) {
        this._params.vco1PulseWidth = pw;
        if (this._params.vco1Wave === 'pulse') {
            this.vco1.width.value = pw;
        }
    }

    // VCO 2
    setVco2Wave(wave) {
        this._params.vco2Wave = wave;
        this.vco2.type = wave;
    }

    setVco2Fine(cents) {
        this._params.vco2Fine = cents;
        this.vco2.detune.value = cents;
    }

    setVco2Level(level) {
        this._params.vco2Level = level;
        this.vco2Gain.gain.value = level;
    }

    setVco2PulseWidth(pw) {
        this._params.vco2PulseWidth = pw;
        if (this._params.vco2Wave === 'pulse') {
            this.vco2.width.value = pw;
        }
    }

    // VCO 3
    setVco3Wave(wave) {
        this._params.vco3Wave = wave;
        this.vco3.type = wave;
    }

    setVco3Fine(cents) {
        this._params.vco3Fine = cents;
        this.vco3.detune.value = cents;
    }

    setVco3Level(level) {
        this._params.vco3Level = level;
        this.vco3Gain.gain.value = level;
    }

    setVco3LowFreq(enabled) {
        this._params.vco3LowFreq = enabled;
        if (enabled) {
            // Switch to LF mode (0.1 - 30 Hz)
            this.vco3.frequency.value = 5;
        }
    }

    setVco3PulseWidth(pw) {
        this._params.vco3PulseWidth = pw;
        if (this._params.vco3Wave === 'pulse') {
            this.vco3.width.value = pw;
        }
    }

    // Noise
    setNoiseType(type) {
        this._params.noiseType = type;
        this.noise.type = type;
    }

    setNoiseLevel(level) {
        this._params.noiseLevel = level;
        this.noiseGain.gain.value = level;
    }

    // Ring Mod
    setRingModLevel(level) {
        this._params.ringModLevel = level;
        this.ringModGain.gain.value = level;
    }

    // Filter
    setFilterCutoff(freq) {
        this._params.filterCutoff = freq;
        this.filter.frequency.value = freq;
    }

    setFilterResonance(q) {
        this._params.filterResonance = q;
        this.filter.Q.value = q;
    }

    setFilterEnvAmount(amount) {
        this._params.filterEnvAmount = amount;
        this.filterEnvGain.gain.value = amount * 5000;
    }

    setFilterKeyTrack(amount) {
        this._params.filterKeyTrack = amount;
    }

    // Envelope 1 (Filter)
    setEnv1Attack(time) {
        this._params.env1Attack = time;
        this.env1.attack = time;
    }

    setEnv1Decay(time) {
        this._params.env1Decay = time;
        this.env1.decay = time;
    }

    setEnv1Sustain(level) {
        this._params.env1Sustain = level;
        this.env1.sustain = level;
    }

    setEnv1Release(time) {
        this._params.env1Release = time;
        this.env1.release = time;
    }

    // Envelope 2 (VCA)
    setEnv2Attack(time) {
        this._params.env2Attack = time;
        this.env2.attack = time;
    }

    setEnv2Decay(time) {
        this._params.env2Decay = time;
        this.env2.decay = time;
    }

    setEnv2Sustain(level) {
        this._params.env2Sustain = level;
        this.env2.sustain = level;
    }

    setEnv2Release(time) {
        this._params.env2Release = time;
        this.env2.release = time;
    }

    // LFO
    setLfoRate(rate) {
        this._params.lfoRate = rate;
        this.lfo.frequency.value = rate;
    }

    setLfoWave(wave) {
        this._params.lfoWave = wave;
        this.lfo.type = wave;
    }

    setLfoToVco(amount) {
        this._params.lfoToVco = amount;
        this.lfoVcoGain.gain.value = amount * 50;
    }

    setLfoToFilter(amount) {
        this._params.lfoToFilter = amount;
        this.lfoFilterGain.gain.value = amount * 2000;
    }

    setLfoToVca(amount) {
        this._params.lfoToVca = amount;
        this.lfoVcaGain.gain.value = amount * 0.5;
    }

    // Master
    setVolume(vol) {
        this._params.volume = vol;
        if (this.isPowered) {
            this.outputGain.gain.value = vol;
        }
    }

    setPortamento(time) {
        this._params.portamento = time;
    }

    // === Bulk parameter setting ===
    setParams(params) {
        if (params.vco1Wave !== undefined) this.setVco1Wave(params.vco1Wave);
        if (params.vco1Fine !== undefined) this.setVco1Fine(params.vco1Fine);
        if (params.vco1Level !== undefined) this.setVco1Level(params.vco1Level);
        if (params.vco1PulseWidth !== undefined) this.setVco1PulseWidth(params.vco1PulseWidth);

        if (params.vco2Wave !== undefined) this.setVco2Wave(params.vco2Wave);
        if (params.vco2Fine !== undefined) this.setVco2Fine(params.vco2Fine);
        if (params.vco2Level !== undefined) this.setVco2Level(params.vco2Level);
        if (params.vco2PulseWidth !== undefined) this.setVco2PulseWidth(params.vco2PulseWidth);

        if (params.vco3Wave !== undefined) this.setVco3Wave(params.vco3Wave);
        if (params.vco3Fine !== undefined) this.setVco3Fine(params.vco3Fine);
        if (params.vco3Level !== undefined) this.setVco3Level(params.vco3Level);
        if (params.vco3LowFreq !== undefined) this.setVco3LowFreq(params.vco3LowFreq);
        if (params.vco3PulseWidth !== undefined) this.setVco3PulseWidth(params.vco3PulseWidth);

        if (params.noiseType !== undefined) this.setNoiseType(params.noiseType);
        if (params.noiseLevel !== undefined) this.setNoiseLevel(params.noiseLevel);

        if (params.ringModLevel !== undefined) this.setRingModLevel(params.ringModLevel);

        if (params.filterCutoff !== undefined) this.setFilterCutoff(params.filterCutoff);
        if (params.filterResonance !== undefined) this.setFilterResonance(params.filterResonance);
        if (params.filterEnvAmount !== undefined) this.setFilterEnvAmount(params.filterEnvAmount);
        if (params.filterKeyTrack !== undefined) this.setFilterKeyTrack(params.filterKeyTrack);

        if (params.env1Attack !== undefined) this.setEnv1Attack(params.env1Attack);
        if (params.env1Decay !== undefined) this.setEnv1Decay(params.env1Decay);
        if (params.env1Sustain !== undefined) this.setEnv1Sustain(params.env1Sustain);
        if (params.env1Release !== undefined) this.setEnv1Release(params.env1Release);

        if (params.env2Attack !== undefined) this.setEnv2Attack(params.env2Attack);
        if (params.env2Decay !== undefined) this.setEnv2Decay(params.env2Decay);
        if (params.env2Sustain !== undefined) this.setEnv2Sustain(params.env2Sustain);
        if (params.env2Release !== undefined) this.setEnv2Release(params.env2Release);

        if (params.lfoRate !== undefined) this.setLfoRate(params.lfoRate);
        if (params.lfoWave !== undefined) this.setLfoWave(params.lfoWave);
        if (params.lfoToVco !== undefined) this.setLfoToVco(params.lfoToVco);
        if (params.lfoToFilter !== undefined) this.setLfoToFilter(params.lfoToFilter);
        if (params.lfoToVca !== undefined) this.setLfoToVca(params.lfoToVca);

        if (params.volume !== undefined) this.setVolume(params.volume);
        if (params.portamento !== undefined) this.setPortamento(params.portamento);
    }

    // === Cleanup ===
    dispose() {
        this.vco1.stop();
        this.vco2.stop();
        this.vco3.stop();
        this.noise.stop();
        this.lfo.stop();

        this.vco1.dispose();
        this.vco2.dispose();
        this.vco3.dispose();
        this.noise.dispose();
        this.lfo.dispose();
        this.filter.dispose();
        this.env1.dispose();
        this.env2.dispose();
        this.outputGain.dispose();
    }
}
